import { isBoolean, isEmpty, isNil, toLower, trim } from 'lodash'

import { hasChannelAccess } from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import {
  hasKodiEntitlement,
  hasRealtimeCoachingAccess,
  hasLiveListenAccess as liveListenAccess,
} from '@/reducers/auth/currentUser.redux'
import { sidebarSections } from './Sidebar.constants'

export const getTestId = (path) => {
  const removeSlash = path.split('/')
  removeSlash.shift()
  const testIdPath = removeSlash.join('-')

  return `${testIdPath}-link`
}

export const getAccess = (currentUser, currentOrganization, flags) => {
  const isBaltoAdmin = currentUser.organizationid === 1
  const isHierarchyManager = currentUser.hierarchy_manager
  const hasManageSupportAccess = !!currentUser.manage_support_access
  // `balto_cloud_access` is the auth-service-serialized permission name
  // (see auth-service/src/login.py:165). Other consumers in the app also
  // read this field directly (e.g. CSVUploadForms/Preview.js).
  const hasCloudAccess = !!currentUser.balto_cloud_access
  const isAudioIngestionOnly = currentOrganization.audio_ingestion_only
  const hasChildOrgSelected =
    isHierarchyManager && currentUser.organizationid !== currentUser.own_organization_id
  // RTC nav item = the per-user Real-Time Coaching permission, minus
  // audio-ingestion-only orgs (which never surface the live coaching surfaces).
  // Deliberately NOT the Agent Assist check: configuring alerts is the licensed
  // RTC product, while answering a raised hand from the bell is not.
  const hasRTCAccess = hasRealtimeCoachingAccess(currentUser) && !isAudioIngestionOnly
  const hasCopilotAccess = currentOrganization.qa_copilot_enabled && currentUser.edit_qa_copilot
  const hasRTQAccess = currentOrganization.qa_enabled && currentUser.edit_qa
  const hasPlaybookEditAccess = currentUser.edit_config
  const hasNotetakerAccess = hasPlaybookEditAccess && currentOrganization.call_summarization_enabled
  // One source of truth for the flag: the page, the index redirect and the
  // Agent Assist gate all treat only an explicit `false` as "off". The nav used
  // `!!`, so an unresolved LaunchDarkly flag hid the link to a page the user
  // could still reach.
  const hasLiveListenAccess = liveListenAccess(currentOrganization, flags)
  const hasDynamicInsightsAccess = currentOrganization.dynamic_insights_enabled
  const hasComplianceAccess = currentOrganization.compliance_tasks_enabled
  const hasCoachingAccess = currentOrganization.coaching_tasks_enabled
  const hasUploadEmailsAccess = hasChannelAccess(
    currentOrganization.active_channels,
    CHANNEL_TYPE.EMAIL,
    isBaltoAdmin
  )
  const hasPromptEditorAccess = currentUser.prompt_editor
  const hasOutboundCampaignsAccess =
    currentOrganization.outbound_campaigns_enabled && currentUser.outbound_campaign_editor
  // Togo Analytics hosts both the voice-agent and outbound analytics tabs, so the
  // nav item shows when either underlying org flag is on.
  const hasTogoAnalyticsAccess =
    currentOrganization.togo_enabled || currentOrganization.outbound_campaigns_enabled
  // Agent Studio (voice-agent editor) — the `togo_editor` permission only applies
  // to orgs with Togo enabled (see CSVUploadForms/validation.js).
  const hasTogoEditorAccess = currentOrganization.togo_enabled && currentUser.togo_editor
  const hasToolsEditorAccess = currentUser.tools_editor && currentOrganization.tools_enabled
  const hasAlexInsightsAccess = currentOrganization.alex_insights_enabled
  // Two independent gates: the LD flag is Balto's rollout, the entitlement is the
  // org's own setting plus any per-user override. Both must pass.
  const hasKodiAccess =
    !!flags.baltoCoworkEnabled && !isBaltoAdmin && hasKodiEntitlement(currentUser)
  const hasFeatureFeedbackAccess = !!flags.featureFeedbackEnabled

  return {
    isBaltoAdmin,
    isHierarchyManager,
    hasCloudAccess,
    hasChildOrgSelected,
    hasRTCAccess,
    hasCopilotAccess,
    hasRTQAccess,
    hasNotetakerAccess,
    hasLiveListenAccess,
    hasDynamicInsightsAccess,
    hasComplianceAccess,
    hasCoachingAccess,
    hasUploadEmailsAccess,
    hasPlaybookEditAccess,
    isAudioIngestionOnly,
    hasPromptEditorAccess,
    hasOutboundCampaignsAccess,
    hasTogoAnalyticsAccess,
    hasTogoEditorAccess,
    hasToolsEditorAccess,
    hasAlexInsightsAccess,
    hasKodiAccess,
    hasManageSupportAccess,
    hasFeatureFeedbackAccess,
  }
}

const filterLinks = (links, access) => {
  const {
    isBaltoAdmin,
    isHierarchyManager,
    hasCloudAccess,
    hasRTCAccess,
    hasCopilotAccess,
    hasRTQAccess,
    hasNotetakerAccess,
    hasLiveListenAccess,
    hasDynamicInsightsAccess,
    hasComplianceAccess,
    hasCoachingAccess,
    hasUploadEmailsAccess,
    hasPlaybookEditAccess,
    isAudioIngestionOnly,
    hasPromptEditorAccess,
    hasOutboundCampaignsAccess,
    hasTogoAnalyticsAccess,
    hasTogoEditorAccess,
    hasToolsEditorAccess,
    hasAlexInsightsAccess,
    hasKodiAccess,
    hasManageSupportAccess,
    hasFeatureFeedbackAccess,
  } = access

  const filteredLinks = links.filter((link) => {
    // Available to all users
    if (
      link.path.startsWith('/supervisor-usage') ||
      link.path.startsWith('/reports') ||
      link.path.startsWith('/playbooks') ||
      link.path.startsWith('/leaderboards') ||
      link.path.startsWith('/playlists') ||
      link.path.startsWith('/explorer') ||
      link.path.startsWith('/ai-answers') ||
      link.path.startsWith('/balto-academy')
    ) {
      return true
    }

    // QA Inbox
    if (link.path === '/qa-copilot/inbox') {
      return isBaltoAdmin || hasCopilotAccess || hasRTQAccess
    }

    // Calibration (unified prompt engineering + customer feedback)
    if (link.path.startsWith('/quality/calibration')) {
      return isBaltoAdmin || hasCopilotAccess || hasPromptEditorAccess
    }

    // Balto Admin only
    if (
      link.path === '/organizations' ||
      link.path === '/erase_customer_data' ||
      link.path.startsWith('/coaching/scorecards')
    ) {
      return isBaltoAdmin
    }

    // Tools Editor access
    if (link.path === '/tools') {
      return isBaltoAdmin || hasToolsEditorAccess
    }

    // Users only
    if (link.path === '/users') {
      return !isBaltoAdmin
    }

    if (link.path === '/command-center' || link.path === '/live-listen') {
      return (isBaltoAdmin || hasLiveListenAccess) && !isAudioIngestionOnly
    }

    // RTC
    if (link.path.startsWith('/realtime_coaching')) {
      return (isBaltoAdmin || hasRTCAccess) && !isAudioIngestionOnly
    }

    // Copilot
    if (link.path === '/qa-copilot/enterprise') {
      return isBaltoAdmin || isHierarchyManager
    }

    if (link.path.startsWith('/qa-copilot')) {
      return isBaltoAdmin || hasCopilotAccess
    }

    // RTQ
    if (link.path.startsWith('/scorecards')) {
      return isBaltoAdmin || hasRTQAccess
    }

    if (link.path.startsWith('/notetaker')) {
      return isBaltoAdmin || hasNotetakerAccess
    }

    if (link.path === '/kodi' || link.path === '/kodi-reports') {
      return hasKodiAccess
    }

    if (link.path.startsWith('/agentic-insights')) {
      return isBaltoAdmin || hasAlexInsightsAccess
    }

    if (link.path.startsWith('/discover') || link.path.startsWith('/trends')) {
      return hasDynamicInsightsAccess
    }

    if (link.path.startsWith('/compliance')) {
      return hasComplianceAccess
    }

    if (link.path.startsWith('/coaching')) {
      return hasCoachingAccess
    }

    if (link.path.startsWith('/upload-interactions') || link.path.startsWith('/upload-calls')) {
      return true
    }

    if (link.path.startsWith('/upload-emails')) {
      return hasUploadEmailsAccess
    }

    if (link.path.startsWith('/campaigns') || link.path.startsWith('/contact-lists')) {
      return hasOutboundCampaignsAccess
    }

    // Togo
    if (link.path.startsWith('/togo/analytics')) {
      return isBaltoAdmin || hasTogoAnalyticsAccess
    }

    if (link.path === '/voice-agents') {
      return isBaltoAdmin || hasTogoEditorAccess
    }

    if (link.path.startsWith('/playbook_mapping')) {
      return hasPlaybookEditAccess
    }

    // Omnichannel Usage — manager-only ("Balto Cloud Access" = user has a password)
    if (link.path === '/omnichannel-usage') {
      return isBaltoAdmin || hasCloudAccess
    }

    // Balto Support Access — visible only to users with manage_support_access,
    // and never to org-1 (Balto admins use the banner instead).
    if (link.path === '/support-access') {
      return !isBaltoAdmin && hasManageSupportAccess
    }

    if (link.path === '/feature-feedback') {
      return isBaltoAdmin || hasFeatureFeedbackAccess
    }

    // No admin exception, unlike most rows here. The reporting API's
    // require_balto_cloud_access has none either, so a Balto admin without
    // Cloud Access would follow this link to a page whose every call 403s.
    if (link.path === '/exports') {
      return hasCloudAccess
    }

    return isBaltoAdmin
  })

  return filteredLinks
}

export const getSidebarData = (currentOrganization, currentUser, flags) => {
  const access = getAccess(currentUser, currentOrganization, flags)

  return sidebarSections.map((section) => ({
    ...section,
    links: filterLinks(section.links, access),
  }))
}

export const getFavoriteLinks = (sections, favoritePaths) => {
  const allLinks = sections.flatMap((section) => section.links)

  return favoritePaths
    .map((path) => allLinks.find((link) => link.path === path))
    .filter((link) => !isNil(link))
}

// Mirrors NavLink's active-state matching (custom isActive, exact, nested
// routes) so the section scroll-pin agrees with the highlighted link
export const neverActive = () => false

export const linkMatchesPath = (link, pathname) => {
  if (link.isActive) {
    return link.isActive(null, { pathname })
  }

  if (link.exact) {
    return link.path === pathname
  }

  return pathname === link.path || pathname.startsWith(`${link.path}/`)
}

export const isSectionOpen = (sectionState, accessor) => {
  if (isBoolean(sectionState[accessor])) {
    return sectionState[accessor]
  }

  return true
}

export const filterSectionsBySearch = (sections, searchQuery) => {
  const query = toLower(trim(searchQuery))

  if (isEmpty(query)) {
    return sections
  }

  return sections.map((section) => ({
    ...section,
    links: section.links.filter((link) => toLower(link.label).includes(query)),
  }))
}
